import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, of, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  Auth,
  AuthProvider,
  confirmPasswordReset,
  getIdToken,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithRedirect,
  updatePassword,
} from '@angular/fire/auth';
import { AuthCode, AuthResult } from '../../model/auth.model';
import { convertFirebaseError } from '../../utils/firebase-error.utils';
import { User } from '@firebase/auth';

/**
 * Service contenant toutes les méthodes permettant d'utiliser la lib firebase
 * @scope Ne doit pas être appelé en direct mais dans des services auth ou password
 */
@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private readonly firebaseAuth = inject(Auth);

  getCurrentUser(): User | null {
    return this.firebaseAuth.currentUser;
  }

  /**
   * Méthode permettant d'actualiser le token Firebase lors de l'authentification
   * La récupération du token est forcée pour être sûre d'obtenir le token de la personne qui se connecte
   *
   * @returns {Observable<string | null>} - Un token si l'utilisateur est connecté, sinon null (cas qui ne devrait pas arriver)
   */
  getFirebaseToken(): Observable<string | null> {
    if (this.firebaseAuth.currentUser) {
      // getIdToken a un second paramètre pour forcer le refresh du token
      // il est impératif de le conserver pour permettre le changement d'utilisateur quand on se déconnecte-reconnecte
      return from(getIdToken(this.firebaseAuth.currentUser, true));
    } else {
      // Cas d'erreur qui ne doit pas arriver
      return of(null);
    }
  }

  /**
   * Connecte un utilisateur à l'aide de son email et de son mot de passe.
   *
   * @param {string} email - L'adresse e-mail de l'utilisateur utilisée pour demander le lien de connexion.
   * @param {string} password - Le mot de passe de l'utilisateur
   *
   * @returns {Observable<AuthResult>}
   * Un flux RxJS qui émet un objet :
   * ```ts
   * {
   *   code: AuthCode.success,        // Indique le succès de la connexion
   *   userCredential: UserCredential // Objet utilisateur retourné par Firebase
   * }
   * ```
   */
  signInWithEmailAndPassword$(email: string, password: string): Observable<AuthResult> {
    return from(signInWithEmailAndPassword(this.firebaseAuth, email, password)).pipe(
      map(
        userCredential =>
          ({
            code: AuthCode.success,
            userCredential,
          }) as AuthResult,
      ),
      catchError(error => throwError(() => convertFirebaseError(error, error?.code))),
    );
  }

  /**
   * Connecte un utilisateur à l'aide d'un lien de connexion envoyé par e-mail.
   *
   * @param {string} email - L'adresse e-mail de l'utilisateur utilisée pour demander le lien de connexion.
   *                         Doit correspondre à l'adresse utilisée lors de l'appel de `sendSignInLinkToEmail`.
   * @param {string} link - Le lien complet de connexion reçu par e-mail.
   *                        Il contient les paramètres Firebase (mode, oobCode, apiKey, etc.).
   *                        En général, c’est `window.location.href` après le clic sur le lien.
   *
   * @returns {Observable<AuthResult>}
   * Un flux RxJS qui émet un objet :
   * ```ts
   * {
   *   code: AuthCode.success,        // Indique le succès de la connexion
   *   userCredential: UserCredential // Objet utilisateur retourné par Firebase
   * }
   * ```
   */
  signInWithEmailLink$(email: string, link: string): Observable<AuthResult> {
    return from(signInWithEmailLink(this.firebaseAuth, email, link)).pipe(
      map(
        userCredential =>
          ({
            code: AuthCode.success,
            userCredential,
          }) as AuthResult,
      ),
      catchError(error => throwError(() => convertFirebaseError(error, error?.code))),
    );
  }

  /**
   * Démarre le flux de connexion OAuth avec redirection pour un fournisseur donné.
   *
   * Cette méthode utilise Firebase `signInWithRedirect` pour rediriger l'utilisateur
   * vers la page de connexion du fournisseur (Google, Facebook, etc.).
   * Après la connexion, l'utilisateur sera redirigé vers l'application.
   *
   * @param {AuthProvider} provider - Le fournisseur d'authentification à utiliser
   *                                   (ex. `new GoogleAuthProvider()`).
   * @returns {Observable<void>}
   * Un Observable encapsulant le Promise de Firebase.
   * Note : l'utilisateur n'est pas encore connecté à ce moment,
   *       il faut utiliser `getRedirectResult$()` après la redirection pour obtenir le résultat.
   *
   * @example
   * ```ts
   * this.authService.signInWithRedirect$(new GoogleAuthProvider()).subscribe();
   * ```
   */

  signInWithRedirect$(provider: AuthProvider): Observable<void> {
    return from(signInWithRedirect(this.firebaseAuth, provider));
  }

  /**
   * Récupère le résultat de la connexion OAuth après une redirection
   * (suite à l'appel à signInWithRedirect).
   *
   * @returns {Observable<AuthResult>}
   * Un Observable qui émet un `AuthResult` contenant :
   * - `code: AuthCode.success` si la connexion a réussi
   * - `userCredential: UserCredential` retourné par Firebase
   * - `null` si aucun utilisateur n'est connecté
   *
   * @example
   * ```ts
   * this.authService.getRedirectResult$().subscribe(result => {
   *   if(result?.code === AuthCode.success) {
   *     console.log('Utilisateur connecté', result.userCredential.user);
   *   } else {
   *     console.log('Aucun utilisateur connecté');
   *   }
   * });
   * ```
   */
  getRedirectResult$(): Observable<AuthResult | null> {
    return from(getRedirectResult(this.firebaseAuth)).pipe(
      map(userCredential => {
        if (userCredential?.user) {
          return {
            code: AuthCode.success,
            userCredential,
          } as AuthResult;
        }
        return null;
      }),
      catchError(error => throwError(() => convertFirebaseError(error, error?.code))),
    );
  }

  /**
   * Met à jour le mot de passe de l'utilisateur en passant un code OOB récupérer par un appel backend effectué avant
   * @param oobCode - Code Oob récupérer du back end pour assurer l'intégrité de celui-ci
   * @param newPassword - Nouveau mot de passe de l'utilisateur
   */
  updatePasswordByUsingOobCode$(oobCode: string, newPassword: string): Observable<void> {
    return from(confirmPasswordReset(this.firebaseAuth, oobCode, newPassword)).pipe(map(() => undefined));
  }

  /**
   * Méthode qui vérifie que le mot de passe actuel est correct et met à jour le mot de passe
   * @param userEmail - Email de l'utilisateur
   * @param currentPassword - Mot de passe actuel
   * @param newPassword - Nouveau mot de passe
   */
  updatePassword$(userEmail: string, currentPassword: string, newPassword: string): Observable<AuthResult> {
    // Check the currentPassword by reauthenticate the current user by using email and password
    return from(signInWithEmailAndPassword(this.firebaseAuth, userEmail, currentPassword)).pipe(
      switchMap(userCredential => from(updatePassword(userCredential.user, newPassword))),
      switchMap(() => of({ code: AuthCode.success } as AuthResult)),
      catchError(error => throwError(() => convertFirebaseError(error, error?.code))),
    );
  }
}
